import { useLocation, useNavigate } from "react-router";
import React, { useState, useEffect, useRef } from "react";
import { AiOutlineMenu, AiOutlineClose } from 'react-icons/ai';
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

const MainNav = ({ lang }) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [viniOpen, setViniOpen] = useState(false);

    const [langDropdownOpen, setLangDropdownOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();     // ✅ dentro al componente React

    const { t, i18n } = useTranslation();
    const langDropdownRef = useRef(null);

    const prefix = `/${lang}`;

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
        if (langDropdownOpen) setLangDropdownOpen(false);
    };

    const toggleLangDropdown = () => {
        if (!menuOpen) {
            setLangDropdownOpen(!langDropdownOpen);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
                setLangDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [langDropdownRef]);

    const supportedLanguages = [
        { code: "it", name: "Italiano", flag: "https://upload.wikimedia.org/wikipedia/commons/0/03/Flag_of_Italy.svg" },
        { code: "en", name: "English", flag: "https://upload.wikimedia.org/wikipedia/en/a/a4/Flag_of_the_United_States.svg" },
        { code: "fr", name: "Français", flag: "https://upload.wikimedia.org/wikipedia/en/c/c3/Flag_of_France.svg" },
        { code: "de", name: "Deutsch", flag: "https://upload.wikimedia.org/wikipedia/en/b/ba/Flag_of_Germany.svg" },
        { code: "es", name: "Español", flag: "https://upload.wikimedia.org/wikipedia/en/9/9a/Flag_of_Spain.svg" },
    ];

    const currentLanguageFullCode = i18n.language;
    const currentLanguageBaseCode = currentLanguageFullCode.split('-')[0];
    const currentLanguage = supportedLanguages.find(lang => lang.code === currentLanguageBaseCode) ||
        supportedLanguages.find(lang => lang.code === 'it');



    const changeLanguageHandler = (langCode) => {
        console.log("Cambiando lingua a:", langCode);
        const pathWithoutLang = location.pathname.split("/").slice(2).join("/") || "";
        navigate(`/${langCode}/${pathWithoutLang}`);
        i18n.changeLanguage(langCode);
        setLangDropdownOpen(false);
    };

    const LanguageSwitcher = ({ isMobile = false }) => {
        if (isMobile) {
            return (
                <div className="flex items-center justify-center space-x-3 py-2">
                    {supportedLanguages.map((lang) => (
                        <button
                            key={lang.code}
                            onClick={() => changeLanguageHandler(lang.code)}
                            className={clsx(
                                "p-1 rounded-sm transition-all duration-200 ease-in-out",
                                "focus:outline-none focus:ring-2 focus:ring-pesca focus:ring-offset-2 focus:ring-offset-verde_scuro",
                                lang.code === currentLanguage.code
                                    ? "border-2 border-pesca shadow-md scale-110"
                                    : "border-2 border-transparent hover:opacity-80"
                            )}
                            aria-label={t('navbar.switchLanguage', { lang: lang.name })}
                            disabled={lang.code === currentLanguage.code}
                        >
                            <img src={lang.flag} alt={lang.name} className="w-8 h-auto rounded-sm" />
                        </button>
                    ))}
                </div>
            );
        }

        return (
            <div className="relative" ref={langDropdownRef}>
                <button
                    onClick={toggleLangDropdown}
                    className="flex items-center p-1 rounded-md hover:bg-verde_chiaro/20 focus:outline-none focus:ring-2 focus:ring-pesca transition-colors"
                    aria-expanded={langDropdownOpen}
                    aria-haspopup="true"
                >
                    <img
                        src={currentLanguage.flag}
                        alt={t('navbar.currentLanguageFlag', { lang: currentLanguage.name })}
                        className="w-7 h-auto rounded-sm"
                    />
                    {langDropdownOpen ? <FiChevronUp className="ml-1 text-pesca" /> : <FiChevronDown className="ml-1 text-pesca" />}
                </button>

                {langDropdownOpen && (
                    <div className="absolute mt-2 w-auto bg-verde_scuro rounded-md shadow-lg z-50 py-1 right-0" role="menu">
                        {supportedLanguages.map((lang) => (
                            <button
                                key={lang.code}
                                onClick={() => changeLanguageHandler(lang.code)}
                                className={clsx(
                                    "flex items-center w-full px-4 py-2 text-sm text-pesca hover:bg-verde_chiaro/30 hover:text-verde_chiaro",
                                    { "bg-verde_chiaro/20 font-semibold": lang.code === currentLanguage.code }
                                )}
                                role="menuitem"
                                disabled={lang.code === currentLanguage.code}
                            >
                                <img src={lang.flag} alt={lang.name} className="w-5 h-auto rounded-sm mr-2" />
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <nav className="flex justify-between items-center h-16">
            {/* Mobile Menu Icon */}
            <div className="lg:hidden">
                <button onClick={toggleMenu} className="text-3xl focus:outline-none z-[60] relative p-2">
                    {menuOpen ? <AiOutlineClose className="text-pesca" /> : <AiOutlineMenu className="text-pesca" />}
                </button>
            </div>

            {/* Navbar desktop */}
            <div className="hidden lg:flex items-center mx-8 space-x-4 lg:space-x-6">
                <a
                    href={`${prefix}/`}
                    className={clsx(
                        "text-lg font-normal transition-colors",
                        location.pathname === `${prefix}/` ? "text-verde_chiaro font-medium" : "text-pesca hover:text-verde_chiaro"
                    )}
                >
                    {t("navbar.home")}
                </a>
                <a
                    href={`${prefix}/storia`}
                    className={clsx(
                        "text-lg font-normal transition-colors",
                        location.pathname === `${prefix}/storia` ? "text-verde_chiaro font-medium" : "text-pesca hover:text-verde_chiaro"
                    )}
                >
                    {t("navbar.storia")}
                </a>
                <a
                    href={`${prefix}/vigneto`}
                    className={clsx(
                        "text-lg font-normal transition-colors",
                        location.pathname === `${prefix}/vigneto` ? "text-verde_chiaro font-medium" : "text-pesca hover:text-verde_chiaro"
                    )}
                >
                    {t("navbar.vigneto")}
                </a>
                <div
                    className="relative"

                >
                    <div className="flex items-center">
                        <a
                            href={`${prefix}/vini`}
                            className={clsx(
                                "text-lg font-normal transition-colors flex items-center",
                                location.pathname.startsWith(`${prefix}/vini`)
                                    ? "text-verde_chiaro font-medium"
                                    : "text-pesca hover:text-verde_chiaro"
                            )}
                        >
                            {t("navbar.vini")}

                        </a>
                        <FiChevronDown
                            onMouseEnter={() => setViniOpen(true)}
                            onClick={() => setViniOpen(!viniOpen)}
                            className={clsx(
                                "ml-1 transition-transform text-pesca",
                                viniOpen && "rotate-180 text-verde_chiaro"
                            )}
                        />
                    </div>

                    {/* Dropdown visibile se viniOpen è true */}
                    {viniOpen && (
                        <div className="absolute bg-verde_scuro shadow-verde_scuro_2 rounded-md shadow-lg z-50 mt-2 right-0 min-w-[160px] py-1 animate-fadeIn"
                            onMouseEnter={() => setViniOpen(true)}
                            onMouseLeave={() => setViniOpen(false)}>
                            <a
                                href={`${prefix}/vini/il-biunt`}
                                className={clsx(
                                    "block px-4 py-2 text-pesca hover:text-verde_chiaro hover:bg-verde_chiaro/20 rounded-t-md transition-colors",
                                    location.pathname === `${prefix}/vini/il-biunt` && "bg-verde_chiaro/10 text-verde_chiaro font-medium"
                                )}
                            >
                                Il Biunt
                            </a>
                            <a
                                href={`${prefix}/vini/germoglio`}
                                className={clsx(
                                    "block px-4 py-2 text-pesca hover:text-verde_chiaro hover:bg-verde_chiaro/20 rounded-b-md transition-colors",
                                    location.pathname === `${prefix}/vini/germoglio` && "bg-verde_chiaro/10 text-verde_chiaro font-medium"
                                )}
                            >
                                Germoglio
                            </a>
                        </div>
                    )}
                </div>

                <a
                    href={`${prefix}/contatti`}
                    className={clsx(
                        "text-lg font-normal transition-colors",
                        location.pathname === `${prefix}/contatti` ? "text-verde_chiaro font-medium" : "text-pesca hover:text-verde_chiaro"
                    )}
                >
                    {t("navbar.contatti")}
                </a>
                <LanguageSwitcher isMobile={false} />
            </div>

            {/* Menu a tendina mobile */}
            {menuOpen && (
                <div className="lg:hidden fixed top-0 left-0 w-full bg-verde_scuro shadow-lg z-50 flex flex-col items-center justify-center overflow-y-auto">
                    <div className="w-full max-w-xs space-y-4 py-8 px-4 text-center">
                        <a
                            href={`${prefix}/`}
                            onClick={() => setMenuOpen(false)}
                            className={clsx(
                                "block w-full p-3 text-center text-xl font-medium transition-colors duration-300 ease-in-out rounded-md hover:bg-verde_chiaro/10",
                                location.pathname === `${prefix}/` ? "text-verde_chiaro bg-verde_chiaro/5" : "text-pesca hover:text-verde_chiaro"
                            )}
                        >
                            {t("navbar.home")}
                        </a>
                        <a
                            href={`${prefix}/storia`}
                            onClick={() => setMenuOpen(false)}
                            className={clsx(
                                "block w-full p-3 text-center text-xl font-medium transition-colors duration-300 ease-in-out rounded-md hover:bg-verde_chiaro/10",
                                location.pathname === `${prefix}/storia` ? "text-verde_chiaro bg-verde_chiaro/5" : "text-pesca hover:text-verde_chiaro"
                            )}
                        >
                            {t("navbar.storia")}
                        </a>
                        <a
                            href={`${prefix}/vigneto`}
                            onClick={() => setMenuOpen(false)}
                            className={clsx(
                                "block w-full p-3 text-center text-xl font-medium transition-colors duration-300 ease-in-out rounded-md hover:bg-verde_chiaro/10",
                                location.pathname === `${prefix}/vigneto` ? "text-verde_chiaro bg-verde_chiaro/5" : "text-pesca hover:text-verde_chiaro"
                            )}
                        >
                            {t("navbar.vigneto")}
                        </a>
                        <div className="relative">
                            <div className="flex items-center w-full justify-center">
                                <a
                                    href={`${prefix}/vini`}
                                    className={clsx(
                                        "p-3 text-center text-xl font-medium transition-colors duration-300 ease-in-out rounded-md hover:bg-verde_chiaro/10 flex justify-center items-center",
                                        location.pathname.startsWith(`${prefix}/vini`)
                                            ? "text-verde_chiaro bg-verde_chiaro/5"
                                            : "text-pesca hover:text-verde_chiaro"
                                    )}
                                >
                                    {t("navbar.vini")}

                                </a>
                                {viniOpen ? (
                                    <FiChevronUp className=" text-pesca" onClick={() => setViniOpen(!viniOpen)} />
                                ) : (
                                    <FiChevronDown className=" text-pesca" onClick={() => setViniOpen(!viniOpen)} />
                                )}
                            </div>

                            {viniOpen && (
                                <div className="flex flex-col items-center space-y-2 rounded-xl shadow-xl shadow-verde_scuro_2">
                                    <a
                                        href={`${prefix}/vini/il-biunt`}
                                        onClick={() => setMenuOpen(false)}
                                        className={clsx("block w-full p-2 text-lg text-pesca hover:text-verde_chiaro hover:bg-verde_chiaro/10 rounded-md transition-colors",
                                            location.pathname.startsWith(`${prefix}/vini/il-biunt`) ? "text-verde_chiaro bg-verde_chiaro/5"
                                                : "text-pesca hover:text-verde_chiaro")}
                                    >
                                        Il Biunt
                                    </a>
                                    <a
                                        href={`${prefix}/vini/germoglio`}
                                        onClick={() => setMenuOpen(false)}
                                        className={clsx("block w-full p-2 text-lg text-pesca hover:text-verde_chiaro hover:bg-verde_chiaro/10 rounded-md transition-colors",
                                            location.pathname.startsWith(`${prefix}/vini/germoglio`) ? "text-verde_chiaro bg-verde_chiaro/5"
                                                : "text-pesca hover:text-verde_chiaro")}
                                    >
                                        Germoglio
                                    </a>
                                </div>
                            )}
                        </div>
                        <a
                            href={`${prefix}/contatti`}
                            onClick={() => setMenuOpen(false)}
                            className={clsx(
                                "block w-full p-3 text-center text-xl font-medium transition-colors duration-300 ease-in-out rounded-md hover:bg-verde_chiaro/10",
                                location.pathname === `${prefix}/contatti` ? "text-verde_chiaro bg-verde_chiaro/5" : "text-pesca hover:text-verde_chiaro"
                            )}
                        >
                            {t("navbar.contatti")}
                        </a>

                        <div className="mt-8 pt-6 border-t border-pesca/20">
                            <LanguageSwitcher isMobile={true} />
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default MainNav;
